import { useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * Logo IGM Security.
 *
 * Tenta carregar a imagem em `/logo.png` (na pasta public/).
 * Se não existir, mostra fallback tipográfico ("● igm SECURITY" em Oswald).
 *
 * Pra usar SEU LOGO:
 *   1. Coloque o arquivo do logo em `IGM_App_v2/public/logo.png`
 *      (resolução mínima recomendada: 400×400px, fundo transparente).
 *   2. Reload do app — logo aparece automaticamente.
 *      Sem arquivo, fallback de texto continua funcionando.
 *
 * Tamanhos: 'sm' (compacto), 'md' (default), 'lg' (capa/login).
 */

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Caminho do arquivo do logo. Default: /logo.png em public/ */
  src?: string;
  /**
   * `completa` = marca horizontal (sidebar expandida, login).
   * `compacta` = marca REDUZIDA para a sidebar recolhida (L4 do smoke do
   * Matheus: lá aparecia um "I" solto num círculo, que lia como logo cortado).
   * Tenta `/logo-compacto.png`; sem o arquivo, cai no monograma "IGM" em
   * Oswald — reduzido, mas legível e ainda a marca.
   */
  variante?: 'completa' | 'compacta';
}

const SIZE_CLASSES = {
  sm: { img: 'h-6', text: 'text-base', dot: 'w-2 h-2' },
  md: { img: 'h-8', text: 'text-xl', dot: 'w-3 h-3' },
  lg: { img: 'h-10', text: 'text-2xl', dot: 'w-3 h-3' },
};

export function Logo({
  size = 'md',
  className = '',
  src,
  variante = 'completa',
}: LogoProps) {
  const [imgError, setImgError] = useState(false);
  const s = SIZE_CLASSES[size];
  const arquivo = src ?? (variante === 'compacta' ? '/logo-compacto.png' : '/logo.png');

  // Marca reduzida: quadrada, cabe nos 40px da sidebar recolhida sem virar
  // um borrão. Sem o arquivo próprio, o monograma faz o papel.
  if (variante === 'compacta') {
    if (imgError) {
      // ── O fallback tem de parecer DELIBERADO, não quebrado.
      //
      // A versão anterior era "IGM" a 13px dentro de um círculo de 40px: as
      // três letras encostavam na borda e o conjunto lia como imagem que não
      // carregou. Um fallback que parece erro é pior que um fallback feio —
      // quem vê conclui que o sistema está com defeito.
      //
      // Agora: quadrado de cantos suaves (o mesmo `rounded-xl` dos cards),
      // gradiente entre os dois vermelhos da marca, e o monograma em Oswald
      // com respiro. Usa só token — `red`/`red-deep` e a sombra `shadow-card`
      // do design system —, então acompanha o tema sem ajuste.
      return (
        <div
          className={cn(
            'flex h-10 w-10 flex-shrink-0 select-none items-center justify-center',
            'rounded-xl bg-gradient-to-br from-red to-red-deep shadow-card',
            'font-display text-sm font-semibold leading-none tracking-tight text-white',
            className,
          )}
          aria-label="IGM Security"
          title="IGM Security"
        >
          IGM
        </div>
      );
    }
    return (
      <img
        src={arquivo}
        alt="IGM Security"
        className={cn('h-10 w-10 rounded-full object-contain', className)}
        onError={() => setImgError(true)}
      />
    );
  }

  // Se a imagem falhou (arquivo não existe), usa fallback de texto
  if (imgError) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className={cn('rounded-full bg-red flex-shrink-0', s.dot)} aria-hidden="true" />
        <span
          className={cn(
            'font-display italic text-ink-1 dark:text-ink-dark-1 tracking-tight',
            s.text
          )}
        >
          igm <span className="not-italic tracking-wider font-medium">SECURITY</span>
        </span>
      </div>
    );
  }

  return (
    <img
      // `arquivo`, não `src`: o default saiu do parâmetro quando a variante
      // compacta entrou. Com `src` cru aqui o <img> ficava SEM src — o
      // navegador renderizava o texto do `alt` (114px em vez de 38px) e a
      // TopBar estourava 49px no 375px. Pego pelo gate visual.
      src={arquivo}
      alt="IGM Security"
      className={cn(s.img, 'w-auto', className)}
      onError={() => setImgError(true)}
    />
  );
}
